import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { agentService } from '../services/agentService';
import { authservice } from '../../../../../components/api/authservice';
import FilterableMultiSelect from '../../../common/FilterableMultiSelect';
import { ChevronDown } from 'lucide-react';
import { AgentProps } from '../Interfaces/AgentProps';

type AgentGroup = {
  id: string;
  name: string;
};

type Props = {
  agent?: AgentProps;
  onSave?: (updatedAgent: AgentProps) => void;
};

const AgentOverview: React.FC<Props> = ({ agent ,onSave  }) => {
  const isNew = !agent;
  const navigate = useNavigate();

  const [name, setName] = useState(agent?.title || '');
  const [description, setDescription] = useState(agent?.description || '');
  const [tags, setTags] = useState(agent?.tags || []);
  const [newTag, setNewTag] = useState('');
  const [agentGroups, setAgentGroups] = useState<AgentGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>(agent?.agentGroupId || '');
  const [roleOptions, setRoleOptions] = useState<{ label: string; value: string }[]>([]);
  const [roleAccessList, setRoleAccessList] = useState<string[]>(agent?.ownerRoleIds || []);
  const [agentKey, setAgentKey] = useState(agent?.key || '');
  const [agentIconUrl, setAgentIconUrl] = useState(agent?.iconUrl || '');
  const [loading, setLoading] = useState(false);
  const [iconSearch, setIconSearch] = useState('');
  const [showIconDropdown, setShowIconDropdown] = useState(false);

    const iconOptions = [
    // Databases & Storage
    { label: '🗄️ SQL Database', value: '🗄️' },
    { label: '📂 NoSQL Store', value: '📂' },
    { label: '🧮 Indexed DB', value: '🧮' },
    { label: '🛢️ Data Lake', value: '🛢️' },
    { label: '🧾 Query Result', value: '🧾' },
    { label: '📊 Table View', value: '📊' },
    { label: '📑 Schema Explorer', value: '📑' },
    { label: '🧬 Column Inspector', value: '🧬' },
    { label: '🗃️ Record Set', value: '🗃️' },
    { label: '📥 Insert Row', value: '📥' },
    { label: '📤 Export Data', value: '📤' },
    { label: '🔎 Query Executor', value: '🔎' },
    { label: '📜 SQL Script', value: '📜' },
    { label: '📉 Time Series DB', value: '📉' },
    { label: '🧾 CSV Parser', value: '🧾' }, // already used for parsing, but relevant
    { label: '📦 Backup Blob', value: '📦' },
    { label: '🧾 Raw Dump', value: '🧾' },
    { label: '🔐 Secure Vault', value: '🔐' },
    { label: '📁 Data Source', value: '📁' },
    { label: '📚 Knowledge Base', value: '📚' }, // already used
    // LLMs and AI
    { label: '🤖 LLM Bot', value: '🤖' },
    { label: '🧠 AI Model', value: '🧠' },
    { label: '🗣️ Prompt Input', value: '🗣️' },
    { label: '💬 Chat Completion', value: '💬' },
    { label: '📤 Prompt Output', value: '📤' },

    // Vectors and Embeddings
    { label: '🧲 Vector Store', value: '🧲' },
    { label: '🔎 Semantic Search', value: '🔎' },
    { label: '📐 Embeddings', value: '📐' },
    { label: '📚 Knowledge Base', value: '📚' },

    // Databases and Storage
    { label: '🗄️ SQL Database', value: '🗄️' },
    { label: '📂 NoSQL Storage', value: '📂' },
    { label: '💾 Save Record', value: '💾' },
    { label: '🧾 Query Result', value: '🧾' },

    // Parsers and Transforms
    { label: '📄 Document Parser', value: '📄' },
    { label: '🧪 Data Cleaner', value: '🧪' },
    { label: '🧬 JSON Transform', value: '🧬' },
    { label: '🧾 CSV Parser', value: '🧾' },
    { label: '🔤 Text Normalizer', value: '🔤' },

    // Files and Media
    { label: '📦 Upload File', value: '📦' },
    { label: '📥 Download File', value: '📥' },
    { label: '🎧 Audio Input', value: '🎧' },
    { label: '🎥 Video Clip', value: '🎥' },
    { label: '🖼️ Image File', value: '🖼️' },

    // Mailbox and Events
    { label: '📬 Mail Received', value: '📬' },
    { label: '📤 Mail Sent', value: '📤' },
    { label: '📧 Email Parser', value: '📧' },
    { label: '📨 New Message Event', value: '📨' },

    // Outputs and Integrations
    { label: '🌐 Webhook Call', value: '🌐' },
    { label: '🚀 API Request', value: '🚀' },
    { label: '📲 Notify User', value: '📲' },
    { label: '📡 External Service', value: '📡' },
    { label: '💬 Send Chat', value: '💬' },

    // Workflow Logic
    { label: '🔁 Loop', value: '🔁' },
    { label: '🔀 Branch / Switch', value: '🔀' },
    { label: '✅ Condition (If)', value: '✅' },
    { label: '⏳ Wait / Delay', value: '⏳' },
    { label: '⏱️ Timeout', value: '⏱️' },
    { label: '🔚 End Node', value: '🔚' },

    // System Operations
    { label: '⚙️ Config Node', value: '⚙️' },
    { label: '🔧 Setup Action', value: '🔧' },
    { label: '🔒 Auth', value: '🔒' },
    { label: '🧱 Init Block', value: '🧱' },
    { label: '🧩 Plugin Action', value: '🧩' },

    // Logging / Observability
    { label: '📝 Log Step', value: '📝' },
    { label: '🔍 Debug Node', value: '🔍' },
    { label: '🚨 Error Handler', value: '🚨' },
    { label: '📊 Metrics Collector', value: '📊' },

    // Dev Tools
    { label: '🧰 Dev Tools', value: '🧰' },
    { label: '🔬 Inspect Output', value: '🔬' },
    { label: '🧪 Test Node', value: '🧪' },
    { label: '📐 Map Function', value: '📐' },
    { label: '🧵 String Builder', value: '🧵' },

    // Misc Utilities
    { label: '📌 Pin Step', value: '📌' },
    { label: '📍 Location Tag', value: '📍' },
    { label: '💡 Suggestion Node', value: '💡' },
    { label: '💣 Fallback Step', value: '💣' },
    { label: '♻️ Retry Step', value: '♻️' },
    { label: '🔄 Reset Node', value: '🔄' },

    // Cloud & Infra
    { label: '☁️ Cloud Action', value: '☁️' },
    { label: '📶 Network Node', value: '📶' },
    { label: '🧩 Microservice', value: '🧩' },
    { label: '🏷️ Tag Node', value: '🏷️' },

    // Communication / Human Interaction
    { label: '🧑‍💼 Human-in-the-Loop', value: '🧑‍💼' },
    { label: '🎙️ Voice Input', value: '🎙️' },
    { label: '🗨️ Human Message', value: '🗨️' },
    { label: '🤝 Approve Action', value: '🤝' },

    // Control / Flow
    { label: '🔗 Join Node', value: '🔗' },
    { label: '🔚 Exit Node', value: '🔚' },
    { label: '🛑 Stop Trigger', value: '🛑' },
    { label: '🎛️ Switcher', value: '🎛️' },

    // Time / Scheduling
    { label: '📅 Schedule Trigger', value: '📅' },
    { label: '🕒 Cron Job', value: '🕒' },
    { label: '📆 Date Parser', value: '📆' },
    { label: '⏰ Time Trigger', value: '⏰' },

    // AI tools
    { label: '📖 RAG Retrieval', value: '📖' },
    { label: '🧾 LangChain Step', value: '🧾' },
    { label: '📌 Agent Memory', value: '📌' },
    { label: '🛸 Autonomous Agent', value: '🛸' }
  ];


  const filteredIcons = iconOptions.filter((icon) =>
    icon.label.toLowerCase().includes(iconSearch.toLowerCase())
  );

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const groups = await agentService.getAgentGroups();
        setAgentGroups(groups);
        if (!agent && groups.length > 0) setSelectedGroup(groups[0].id);
      } catch (err) {
        console.error('Failed to fetch agent groups', err);
      }
    };

    fetchGroups();
  }, [agent]);

  useEffect(() => {
    const fetchRoles = async () => {
      setLoading(true);
      try {
        const roles = await authservice.getRoles();
        setRoleOptions(roles.map((r: any) => ({ label: r.roleName, value: r.roleId })));
      } catch (err) {
        console.error('Failed to fetch roles', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    const generateNextKey = async () => {
      try {
        const agents = await agentService.getAgentList();
        const numericKeys = agents
          .map((a: any) => a.key)
          .filter((k: string) => /^AG-\d{3}$/.test(k))
          .map((k: string) => parseInt(k.replace('AG-', ''), 10));

        const max = numericKeys.length ? Math.max(...numericKeys) : 0;
        const next = `AG-${(max + 1).toString().padStart(3, '0')}`;
        setAgentKey(next);
      } catch (err) {
        console.error('Failed to generate key', err);
      }
    };

    if (!agent) generateNextKey();
  }, [agent]);

  const handleIconSelect = (value: string) => {
    setAgentIconUrl(value);
    setShowIconDropdown(false);
    setIconSearch('');
  };

  const addTag = () => {
    const clean = newTag.trim();
    if (clean && !tags.includes(clean)) {
      setTags([...tags, clean]);
      setNewTag('');
    }
  };

  const handleCancel = () => {
    navigate('/portal/agents');
  };

  const handleSave = async () => {
    const payload = {
      id: agent?.id,
      tenantId: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
      key: agentKey,
      title: name,
      inputReferenceId: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
      agentGroupID: selectedGroup,
      iconUrl: agentIconUrl,
      categoryType: 0,
      description,
      tags,
      ownerRoleIds: roleAccessList,
      llmConfigIds: [],
      vectorStoreIds: [],
      knowledgeHubIds: [],
      inputType: 0,
      inputJsonFormat: '',
      outputType: 0,
      outputJsonFormat: '',
      masterPrompt: '',
      chatContext: '',
      createdBy: 'system',
      agenturl: "Not Available",
      operationmode: 1
    };

    try {
      if (isNew) {
       const result = await agentService.createAgent(payload);
      if (result?.id) {
         navigate(`/portal/agentStudio/${result.id}`);
      }
      } else {
        await agentService.updateAgent(agent?.id, payload);
      } 
    } catch (err) {
      console.error('Failed to save agent', err);
    }
  };

  return (
    <div className="w-full h-full bg-black text-white p-10 overflow-y-auto">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold mb-1">
            {isNew ? 'Create a new function' : 'Function overview'}
          </h1>
          <p className="text-xs text-gray-400">Define and configure metadata for this function.</p>
        </div>

        <form className="space-y-6">
          {!isNew && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Agent ID</label>
              <p className="text-xs text-gray-300">{agent?.id}</p>
            </div>
          )}

          <div>
            <label className="block text-xs text-gray-400 mb-1">Agent Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Agent name"
              className="w-full bg-neutral-900 px-3 py-2 rounded-md text-sm border border-neutral-700"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Agent Key</label>
            <input
              value={agentKey} onChange={(e) => setAgentKey(e.target.value)} 
              className="w-full bg-neutral-900 px-3 py-2 rounded-md text-sm border border-neutral-700"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Description</label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this agent do?"
              className="w-full bg-neutral-900 px-3 py-2 rounded-md text-sm border border-neutral-700"
            />
          </div>

          {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> */}
          {/* Operation Mode */}
          {/* <div className="relative">
            <label className="block text-xs text-gray-400 mb-1">Operation Mode</label>
            <select
              className="w-full bg-neutral-900 px-3 py-2 rounded-md text-sm border border-neutral-700"
            >
              <option value="mode1">Standalone App (subdomain)</option>
              <option value="mode2">Workflow Node</option>
              <option value="mode3">Hybrid</option>
            </select>
          </div>
 
          <div className="relative">
            <label className="block text-xs text-gray-400 mb-1">URL</label>
            <input
              className="w-full bg-neutral-900 px-3 py-2 rounded-md text-sm border border-neutral-700"
            />
          </div>
        </div> */}


          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-xs text-gray-400 mb-1">Icon</label>
              <button
                type="button"
                className="w-full bg-neutral-900 px-3 py-2 rounded-md text-sm border border-neutral-700 flex justify-between items-center"
                onClick={() => setShowIconDropdown(!showIconDropdown)}
              >
                <span>
                  {agentIconUrl
                    ? iconOptions.find((i) => i.value === agentIconUrl)?.label
                    : '-- Select an icon --'}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {showIconDropdown && (
  <div className="absolute z-50 w-full mt-1 bg-neutral-900 border border-neutral-700 rounded-md max-h-60 overflow-y-auto shadow-lg">
    <input
      className="w-full bg-neutral-800 text-white px-3 py-2 text-sm border-b border-neutral-700"
      placeholder="Search icons..."
      value={iconSearch}
      onChange={(e) => setIconSearch(e.target.value)}
    />

    {iconOptions.filter(icon =>
      icon.label.toLowerCase().includes(iconSearch.toLowerCase())
    ).length > 0 ? (
      iconOptions
        .filter(icon =>
          icon.label.toLowerCase().includes(iconSearch.toLowerCase())
        )
        .map((icon) => (
          <div
            key={icon.value}
            onClick={() => handleIconSelect(icon.value)}
            className="px-3 py-2 text-sm hover:bg-neutral-700 cursor-pointer"
          >
            {icon.label}
          </div>
        ))
    ) : (
      <div className="px-3 py-2 text-sm text-gray-400">No matching icons</div>
    )}
  </div>
)}

            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-1">Agent Group</label>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="w-full bg-neutral-900 px-3 py-2 rounded-md text-sm border border-neutral-700"
              >
                {agentGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <FilterableMultiSelect
            label="Role Access"
            options={roleOptions}
            selected={roleAccessList}
            setSelected={setRoleAccessList}
          />

         <div>
  <label className="text-gray-400 block mb-1">Tags</label>
  <div className="flex gap-2 flex-wrap items-center">
    {tags.map((tag) => (
      <span
        key={tag}
        className="bg-neutral-800 text-gray-300 px-3 py-1 rounded-full text-xs flex items-center gap-1"
      >
        {tag}
        <button
          type="button"
          onClick={() => setTags(tags.filter((t) => t !== tag))}
          className="text-red-400 hover:text-red-200 ml-1 text-xs"
          title="Remove tag"
        >
          ×
        </button>
      </span>
    ))}
    <input
      type="text"
      placeholder="Add tag..."
      value={newTag}
      onChange={(e) => setNewTag(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          addTag();
        }
      }}
      className="bg-neutral-800 px-3 py-1 rounded text-white text-xs"
    />
    <button
      type="button"
      onClick={addTag}
      className="text-indigo-400 text-xs"
    >
      Add
    </button>
  </div>
</div>

        </form>

        <div className="flex gap-4 mt-10">
          <button
            onClick={handleSave}
            className="px-4 py-2 rounded-md bg-green-600 hover:bg-green-700 text-xs font-medium shadow"
          >
            {isNew ? 'Create Agent' : 'Save Changes'}
          </button>
          <button
            onClick={handleCancel}
            className="px-4 py-2 rounded-md bg-neutral-800 hover:bg-neutral-700 text-xs font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentOverview;


// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { agentService } from '../services/agentService';
// import { authservice } from '../../../../../components/api/authservice';
// import FilterableMultiSelect from '../../../common/FilterableMultiSelect';
// import { ChevronDown } from 'lucide-react';
// import { AgentProps } from '../Interfaces/AgentProps';

// type AgentGroup = {
//   id: string;
//   name: string;
// };

// type Agent = {
//   id: string;
//   name: string;
//   description?: string;
//   status?: 'Active' | 'Disabled';
//   tags: string[];
//   createdBy?: string;
//   createdAt?: string;
//   purpose?: string;
//   useCase?: string;
//   businessUnit?: string;
// };

// type Props = {
//   agent?: AgentProps;
// };

// const AgentOverview: React.FC<Props> = ({ agent }) => {
//   const isNew = !agent;
//   const navigate = useNavigate();

//   const [name, setName] = useState(agent?.title || '');
//   const [description, setDescription] = useState(agent?.description || '');
//   const [tags, setTags] = useState(agent?.tags || []);
//   const [newTag, setNewTag] = useState('');  

//   const [agentGroups, setAgentGroups] = useState<AgentGroup[]>([]);
//   const [selectedGroup, setSelectedGroup] = useState<string>('');

//   const [roleOptions, setRoleOptions] = useState<{ label: string; value: string }[]>([]);
//   const [roleAccessList, setRoleAccessList] = useState<string[]>([]);
//   const [agentKey, setAgentKey] = useState('');

//   const [loading, setLoading] = useState(false);

//   const [agentIconUrl, setAgentIconUrl] = useState('');

//   // 1. Fetch agent groups
//   useEffect(() => {
//     const fetchGroups = async () => {
//       try {
//         const groups = await agentService.getAgentGroups();
//         setAgentGroups(groups);
//         if (groups.length > 0) setSelectedGroup(groups[0].id);
//       } catch (err) {
//         console.error('Failed to fetch agent groups', err);
//       }
//     };

//     fetchGroups();
//   }, []);

//   // 2. Fetch roles and users
//   useEffect(() => {
//     const fetchRolesAndUsers = async () => {
//       setLoading(true);
//       try {
//         const roles = await authservice.getRoles();
//         const users = await authservice.getUsers();
//         setRoleOptions(roles.map((r: any) => ({ label: r.roleName, value: r.roleId })));
//       } catch (err) {
//         console.error('Failed to fetch roles or users', err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchRolesAndUsers();
//   }, []);

//   // 3. Generate new agent key if agent is undefined (i.e., new agent)
//   useEffect(() => {
//     const generateNextKey = async () => {
//       try {
//         const agents = await agentService.getAgentList(); 
//         setAgentKey(`AG-${agents.length+1}`);

//       } catch (err) {
//         console.error('Failed to generate agent key', err);
//       }
//     };

//     if (!agent) {
//       generateNextKey();
//     }
//   }, [agent]);

//   const handleCancel = () => {
//     navigate('/portal/agents');
//   };
//   const iconOptions = [
//     // Databases & Storage
//     { label: '🗄️ SQL Database', value: '🗄️' },
//     { label: '📂 NoSQL Store', value: '📂' },
//     { label: '🧮 Indexed DB', value: '🧮' },
//     { label: '🛢️ Data Lake', value: '🛢️' },
//     { label: '🧾 Query Result', value: '🧾' },
//     { label: '📊 Table View', value: '📊' },
//     { label: '📑 Schema Explorer', value: '📑' },
//     { label: '🧬 Column Inspector', value: '🧬' },
//     { label: '🗃️ Record Set', value: '🗃️' },
//     { label: '📥 Insert Row', value: '📥' },
//     { label: '📤 Export Data', value: '📤' },
//     { label: '🔎 Query Executor', value: '🔎' },
//     { label: '📜 SQL Script', value: '📜' },
//     { label: '📉 Time Series DB', value: '📉' },
//     { label: '🧾 CSV Parser', value: '🧾' }, // already used for parsing, but relevant
//     { label: '📦 Backup Blob', value: '📦' },
//     { label: '🧾 Raw Dump', value: '🧾' },
//     { label: '🔐 Secure Vault', value: '🔐' },
//     { label: '📁 Data Source', value: '📁' },
//     { label: '📚 Knowledge Base', value: '📚' }, // already used
//     // LLMs and AI
//     { label: '🤖 LLM Bot', value: '🤖' },
//     { label: '🧠 AI Model', value: '🧠' },
//     { label: '🗣️ Prompt Input', value: '🗣️' },
//     { label: '💬 Chat Completion', value: '💬' },
//     { label: '📤 Prompt Output', value: '📤' },

//     // Vectors and Embeddings
//     { label: '🧲 Vector Store', value: '🧲' },
//     { label: '🔎 Semantic Search', value: '🔎' },
//     { label: '📐 Embeddings', value: '📐' },
//     { label: '📚 Knowledge Base', value: '📚' },

//     // Databases and Storage
//     { label: '🗄️ SQL Database', value: '🗄️' },
//     { label: '📂 NoSQL Storage', value: '📂' },
//     { label: '💾 Save Record', value: '💾' },
//     { label: '🧾 Query Result', value: '🧾' },

//     // Parsers and Transforms
//     { label: '📄 Document Parser', value: '📄' },
//     { label: '🧪 Data Cleaner', value: '🧪' },
//     { label: '🧬 JSON Transform', value: '🧬' },
//     { label: '🧾 CSV Parser', value: '🧾' },
//     { label: '🔤 Text Normalizer', value: '🔤' },

//     // Files and Media
//     { label: '📦 Upload File', value: '📦' },
//     { label: '📥 Download File', value: '📥' },
//     { label: '🎧 Audio Input', value: '🎧' },
//     { label: '🎥 Video Clip', value: '🎥' },
//     { label: '🖼️ Image File', value: '🖼️' },

//     // Mailbox and Events
//     { label: '📬 Mail Received', value: '📬' },
//     { label: '📤 Mail Sent', value: '📤' },
//     { label: '📧 Email Parser', value: '📧' },
//     { label: '📨 New Message Event', value: '📨' },

//     // Outputs and Integrations
//     { label: '🌐 Webhook Call', value: '🌐' },
//     { label: '🚀 API Request', value: '🚀' },
//     { label: '📲 Notify User', value: '📲' },
//     { label: '📡 External Service', value: '📡' },
//     { label: '💬 Send Chat', value: '💬' },

//     // Workflow Logic
//     { label: '🔁 Loop', value: '🔁' },
//     { label: '🔀 Branch / Switch', value: '🔀' },
//     { label: '✅ Condition (If)', value: '✅' },
//     { label: '⏳ Wait / Delay', value: '⏳' },
//     { label: '⏱️ Timeout', value: '⏱️' },
//     { label: '🔚 End Node', value: '🔚' },

//     // System Operations
//     { label: '⚙️ Config Node', value: '⚙️' },
//     { label: '🔧 Setup Action', value: '🔧' },
//     { label: '🔒 Auth', value: '🔒' },
//     { label: '🧱 Init Block', value: '🧱' },
//     { label: '🧩 Plugin Action', value: '🧩' },

//     // Logging / Observability
//     { label: '📝 Log Step', value: '📝' },
//     { label: '🔍 Debug Node', value: '🔍' },
//     { label: '🚨 Error Handler', value: '🚨' },
//     { label: '📊 Metrics Collector', value: '📊' },

//     // Dev Tools
//     { label: '🧰 Dev Tools', value: '🧰' },
//     { label: '🔬 Inspect Output', value: '🔬' },
//     { label: '🧪 Test Node', value: '🧪' },
//     { label: '📐 Map Function', value: '📐' },
//     { label: '🧵 String Builder', value: '🧵' },

//     // Misc Utilities
//     { label: '📌 Pin Step', value: '📌' },
//     { label: '📍 Location Tag', value: '📍' },
//     { label: '💡 Suggestion Node', value: '💡' },
//     { label: '💣 Fallback Step', value: '💣' },
//     { label: '♻️ Retry Step', value: '♻️' },
//     { label: '🔄 Reset Node', value: '🔄' },

//     // Cloud & Infra
//     { label: '☁️ Cloud Action', value: '☁️' },
//     { label: '📶 Network Node', value: '📶' },
//     { label: '🧩 Microservice', value: '🧩' },
//     { label: '🏷️ Tag Node', value: '🏷️' },

//     // Communication / Human Interaction
//     { label: '🧑‍💼 Human-in-the-Loop', value: '🧑‍💼' },
//     { label: '🎙️ Voice Input', value: '🎙️' },
//     { label: '🗨️ Human Message', value: '🗨️' },
//     { label: '🤝 Approve Action', value: '🤝' },

//     // Control / Flow
//     { label: '🔗 Join Node', value: '🔗' },
//     { label: '🔚 Exit Node', value: '🔚' },
//     { label: '🛑 Stop Trigger', value: '🛑' },
//     { label: '🎛️ Switcher', value: '🎛️' },

//     // Time / Scheduling
//     { label: '📅 Schedule Trigger', value: '📅' },
//     { label: '🕒 Cron Job', value: '🕒' },
//     { label: '📆 Date Parser', value: '📆' },
//     { label: '⏰ Time Trigger', value: '⏰' },

//     // AI tools
//     { label: '📖 RAG Retrieval', value: '📖' },
//     { label: '🧾 LangChain Step', value: '🧾' },
//     { label: '📌 Agent Memory', value: '📌' },
//     { label: '🛸 Autonomous Agent', value: '🛸' }
//   ];


//   const handleSave = async () => {
//     const payload = {
//       tenantId: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
//       key: agentKey,
//       title: name,
//       inputReferenceId: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
//       agentGroupID: selectedGroup,
//       iconUrl: agentIconUrl,
//       categoryType: 0,
//       description,
//       tags,
//       ownerRoleIds: roleAccessList,
//       llmConfigIds: [],
//       vectorStoreIds: [],
//       knowledgeHubIds: [],
//       inputType: 0,
//       inputJsonFormat: '',
//       outputType: 0,
//       outputJsonFormat: '',
//       masterPrompt: '',
//       chatContext: '',
//       createdBy: 'system',
//     };

//     try {
//       await agentService.createAgent(payload);
//       navigate('/portal/agents');
//     } catch (err) {
//       console.error('Failed to save agent', err);
//     }
//   };

//   const addTag = () => {
//     if (newTag.trim() !== '' && !tags.includes(newTag.trim())) {
//       setTags([...tags, newTag.trim()]);
//       setNewTag('');
//     }
//   };
//   const [iconSearch, setIconSearch] = useState('');
//   const [showIconDropdown, setShowIconDropdown] = useState(false);

//   const filteredIcons = iconOptions.filter((icon) =>
//     icon.label.toLowerCase().includes(iconSearch.toLowerCase())
//   );

//   const handleIconSelect = (value: string) => {
//     setAgentIconUrl(value);
//     setShowIconDropdown(false);
//     setIconSearch('');
//   };

//   return (
//     <div className="w-full h-full bg-black text-white p-10 overflow-y-auto">
//       <div className="max-w-5xl mx-auto">
//         {/* Header */}
//         <div className="mb-8">
//           <h1 className="text-2xl font-semibold mb-1">
//             {isNew ? 'Create a New Agent' : 'Agent Overview'}
//           </h1>
//           <p className="text-xs text-gray-400">
//             Provide essential details to define and manage your agent effectively.
//           </p>
//         </div>

//         {/* Form */}
//         <form className="space-y-6">
//           {!isNew && (
//             <div>
//               <label className="block text-xs text-gray-500 mb-1">Agent ID</label>
//               <p className="text-xs text-gray-300">{agent?.id}</p>
//             </div>
//           )}

//           <div>
//             <label className="block text-xs text-gray-400 mb-1">Agent Name</label>
//             <input
//               className="w-full bg-neutral-900 text-white px-3 py-2 rounded-md text-sm border border-neutral-700"
//               value={name}
//               onChange={(e) => setName(e.target.value)}
//               placeholder="e.g., Contract Validator Bot"
//             />
//           </div>
//           <div>
//             <label className="block text-xs text-gray-400 mb-1">Key</label>
//             <input
//               className="w-full bg-neutral-900 text-white px-3 py-2 rounded-md text-sm border border-neutral-700"
//               value={agentKey}
//               onChange={(e) => setAgentKey(e.target.value)}
//               placeholder="e.g., contract-validator-bot"
//             />
//           </div>

//           <div>
//             <label className="block text-xs text-gray-400 mb-1">Description</label>
//             <textarea
//               rows={3}
//               className="w-full bg-neutral-900 text-white px-3 py-2 rounded-md text-sm border border-neutral-700"
//               value={description}
//               onChange={(e) => setDescription(e.target.value)}
//               placeholder="Briefly describe what this agent is for."
//             />
//           </div>

//           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            

//             <div className="relative">
//               <label className="block text-xs text-gray-400 mb-1">Icon</label>

//               <button
//                 type="button"
//                 className="w-full bg-neutral-900 text-white px-3 py-2 rounded-md text-sm border border-neutral-700 flex justify-between items-center"
//                 onClick={() => setShowIconDropdown(!showIconDropdown)}
//               >
//                 <span>
//                   {agentIconUrl
//                     ? `${iconOptions.find((i) => i.value === agentIconUrl)?.label}`
//                     : '-- Select an icon --'}
//                 </span>
//                 <ChevronDown className="w-4 h-4 text-gray-400" />
//               </button>

//               {showIconDropdown && (
//                 <div className="absolute z-50 w-full mt-1 bg-neutral-900 border border-neutral-700 rounded-md max-h-60 overflow-y-auto shadow-lg">
//                   <input
//                     className="w-full bg-neutral-800 text-white px-3 py-2 text-sm border-b border-neutral-700"
//                     placeholder="Search icons..."
//                     value={iconSearch}
//                     onChange={(e) => setIconSearch(e.target.value)}
//                   />

//                   {filteredIcons.length > 0 ? (
//                     filteredIcons.map((icon) => (
//                       <div
//                         key={icon.value}
//                         onClick={() => handleIconSelect(icon.value)}
//                         className="px-3 py-2 text-sm hover:bg-neutral-700 cursor-pointer"
//                       >
//                         {icon.label}
//                       </div>
//                     ))
//                   ) : (
//                     <div className="px-3 py-2 text-sm text-gray-400">No matching icons</div>
//                   )}
//                 </div>
//               )}
//             </div>

//             <div>
//               <label className="block text-xs text-gray-400 mb-1">Agent Group</label>
//               <select
//                 className="w-full bg-neutral-900 text-white px-3 py-2 rounded-md text-sm border border-neutral-700"
//                 value={selectedGroup}
//                 onChange={(e) => setSelectedGroup(e.target.value)}
//               >
//                 {agentGroups.map((group) => (
//                   <option key={group.id} value={group.id}>
//                     {group.name}
//                   </option>
//                 ))}
//               </select>
//             </div>
//           </div>

//           {/* Role Access */}
//           <FilterableMultiSelect
//             label="Role Access"
//             options={roleOptions}
//             selected={roleAccessList}
//             setSelected={setRoleAccessList}
//           />

//           {/* Tags */}
//           <div>
//             <label className="text-gray-400 block mb-1">Tags</label>
//             <div className="flex gap-2 flex-wrap items-center">
//               {tags.map((tag) => (
//                 <span
//                   key={tag}
//                   className="bg-neutral-800 text-gray-300 px-3 py-1 rounded-full text-xs"
//                 >
//                   {tag}
//                 </span>
//               ))}
//               <input
//                 type="text"
//                 placeholder="Add tag..."
//                 value={newTag}
//                 onChange={(e) => setNewTag(e.target.value)}
//                 onKeyDown={(e) => {
//                   if (e.key === 'Enter') {
//                     e.preventDefault();
//                     addTag();
//                   }
//                 }}
//                 className="bg-neutral-800 px-3 py-1 rounded text-white text-xs"
//               />
//               <button
//                 type="button"
//                 onClick={addTag}
//                 className="text-indigo-400 text-xs"
//               >
//                 Add
//               </button>
//             </div>
//           </div> 
//         </form>

//         {/* Actions */}
//         <div className="flex gap-4 mt-10">
//           <button
//             onClick={handleSave}
//             className="px-4 py-2 rounded-md bg-green-600 hover:bg-green-700 text-xs font-medium shadow"
//           >
//             Save
//           </button>
//           <button
//             onClick={handleCancel}
//             className="px-4 py-2 rounded-md bg-neutral-800 hover:bg-neutral-700 text-xs font-medium"
//           >
//             Cancel
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AgentOverview;
